const MS_PER_MINUTE = 60000;
const DEFAULT_SEM_DESLOCAMENTO_KM_THRESHOLD = 0.5;
const ENABLE_LOCAL_PROVAVEL_REBOQUE_STATUS = false;
const OK_PRIORITY_SCORE_BONUS = 10;

const CLIENT_LEVEL_RANK = Object.freeze({
  OK: 0,
  SEM_DESLOCAMENTO: 1,
  // Extensão local opcional (não faz parte da paridade com o catálogo n8n-code3-resumo-cliente.js).
  PROVAVEL_REBOQUE: 1,
});


const normalizeSpeedDisplay = (value, fallback = 0) => {
  const raw = safeStr(value).trim();
  if (raw) {
    return /km\s*\/\s*h/i.test(raw) ? raw : `${raw} km/h`;
  }
  return `${numBR(Number(fallback) || 0, 2)} km/h`;
};

const getDeviceStableKey = (row = {}) => {
  const rawDeviceId = pick(row, ['deviceId', 'id', 'device', 'DeviceId']);
  const normalizedId = Number(rawDeviceId);
  if (Number.isFinite(normalizedId) && normalizedId > 0) return `id:${normalizedId}`;
  const fallbackName = cleanName(pick(row, ['Dispositivo', 'deviceName', 'name']));
  return `name:${fallbackName || '__SEM_NOME__'}`;
};

const getClientStatus = ({
  statusRaw = '',
  distKm = 0,
  trips = 0,
  emMovMin = 0,
  semDeslocamentoKmThreshold = DEFAULT_SEM_DESLOCAMENTO_KM_THRESHOLD,
  enableLocalProvavelReboqueStatus = ENABLE_LOCAL_PROVAVEL_REBOQUE_STATUS,
} = {}) => {
  const probableTow = enableLocalProvavelReboqueStatus && /reboque/i.test(statusRaw);
  if (probableTow) return { status: 'Provável reboque', level: 'PROVAVEL_REBOQUE' };

  const toFiniteOrZero = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'string' && value.trim() === '') return 0;
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const tripsNum = toFiniteOrZero(trips);
  const distKmNum = toFiniteOrZero(distKm);
  const emMovMinNum = toFiniteOrZero(emMovMin);
  const semDeslocamento = tripsNum === 0 || (distKmNum < semDeslocamentoKmThreshold && emMovMinNum === 0);

  return semDeslocamento
    ? { status: 'Sem Deslocamento', level: 'SEM_DESLOCAMENTO' }
    : { status: 'OK', level: 'OK' };
};

export const safeStr = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return s === 'NaN' ? '' : s;
};

export const cleanName = (s) => String(s ?? '').replace(/\t/g, ' ').replace(/\s+/g, ' ').trim();

export const pick = (obj, names) => {
  for (const n of names) {
    if (obj && Object.prototype.hasOwnProperty.call(obj, n)) {
      const v = obj[n];
      if (v !== null && v !== undefined && String(v).trim() !== '') return v;
    }
  }
  return null;
};

export const ts = (v) => {
  if (typeof v === 'string') {
    const raw = v.trim();
    const hasTimezoneSuffix = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
    if (raw && !hasTimezoneSuffix) {
      const utcCandidate = `${raw}Z`;
      const utcTime = new Date(utcCandidate).getTime();
      if (Number.isFinite(utcTime)) {
        return utcTime;
      }
    }
  }
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
};

export const msParaHorasMin = (ms) => {
  const totalMin = Math.round((Number(ms) || 0) / MS_PER_MINUTE);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h} h ${String(m).padStart(2, '0')} m`;
};

export const numBR = (n, casas = 2) => (Number(n) || 0).toLocaleString('pt-BR', {
  minimumFractionDigits: casas,
  maximumFractionDigits: casas,
});

export const dataHoraBR = (value, timezone = 'America/Sao_Paulo') => {
  const t = ts(value);
  if (t === null) return safeStr(value) || '—';
  return new Date(t).toLocaleString('pt-BR', { timeZone: timezone });
};

export const haversineM = (lat1, lon1, lat2, lon2) => {
  const R = 6371000;
  const toRad = (v) => (v * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

export const pontoConfiavel = (p) => !!(p && p.valid !== false && Number.isFinite(Number(p.latitude)) && Number.isFinite(Number(p.longitude)));
export const getIgnition = (p) => (p?.attributes?.ignition === true ? true : (p?.attributes?.ignition === false ? false : null));
export const getMotion = (p) => p?.attributes?.motion === true;
export const getTrackerIncrementMeters = (p) => {
  const n = Number(p?.attributes?.distance ?? p?.distance);
  return Number.isFinite(n) && n >= 0 ? n : null;
};
export const getTrackerTotalMeters = (p) => {
  const n = Number(p?.attributes?.totalDistance ?? p?.totalDistance);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

export const parsePtNumber = (str) => {
  if (str === null || str === undefined || str === '') return 0;
  if (typeof str === 'number' && Number.isFinite(str)) return str;
  const raw = String(str).trim().replace(/[^\d,.-]/g, '');
  if (!raw) return 0;
  const lastComma = raw.lastIndexOf(',');
  const lastDot = raw.lastIndexOf('.');
  const decimalPos = Math.max(lastComma, lastDot);

  const normalized = decimalPos >= 0
    ? `${raw.slice(0, decimalPos).replace(/[.,]/g, '') || '0'}.${raw.slice(decimalPos + 1).replace(/[.,]/g, '')}`
    : raw.replace(/[.,]/g, '');

  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : 0;
};

export const parseIntegerLoose = (v) => {
  const n = Number(String(v ?? '').replace(/[^\d-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

export const parseDurationToMinutes = (str) => {
  if (typeof str === 'number' && Number.isFinite(str)) {
    return str > 24 ? Math.round(str / 60000) : Math.round(str * 60);
  }
  if (!str) return 0;
  const s = String(str).trim();
  const hm = s.match(/(\d+)\s*h\s*(\d+)\s*m/i);
  if (hm) return (Number(hm[1]) || 0) * 60 + (Number(hm[2]) || 0);
  const om = s.match(/(\d+)\s*m/i);
  if (om) return Number(om[1]) || 0;
  return 0;
};

export const minutesToHuman = (mins) => msParaHorasMin((Number(mins) || 0) * MS_PER_MINUTE);

const normalizeRow = (d, timezone, semDeslocamentoKmThreshold = DEFAULT_SEM_DESLOCAMENTO_KM_THRESHOLD) => {
  const firstVal = pick(d, ['Primeiro movimento', 'Primeiro Movimento', 'Data de Início', 'Data Inicio', 'startTime', 'serverTimeStart']);
  const lastVal = pick(d, ['Último movimento', 'Ultimo movimento', 'Último Movimento', 'Data de Fim', 'Data Fim', 'endTime', 'serverTimeEnd']);
  const dist = pick(d, ['Distância', 'Distancia', 'Distância percorrida', 'distance']);
  const viagens = pick(d, ['Viagens', 'Trips', 'qtdViagens', 'Quantidade de viagens', 'Qtd viagens']);
  const emMov = pick(d, ['Em movimento', 'Tempo em movimento', 'Tempo de movimento', 'Tempo movimentando']);
  const statusRaw = safeStr(pick(d, ['status_cliente'])).trim();
  const emMovMinParsed = parseDurationToMinutes(emMov);
  const emMovMinFallback = parseDurationToMinutes(d?.duration);
  const emMovMin = emMovMinParsed || emMovMinFallback;
  const distRawNum = parsePtNumber(dist);
  const distNum = Number.isFinite(Number(d?.distance)) && distRawNum > 100 ? distRawNum / 1000 : distRawNum;
  const viagensNum = parseIntegerLoose(viagens);
  const { status, level } = getClientStatus({
    statusRaw,
    distKm: distNum,
    trips: viagensNum,
    emMovMin,
    semDeslocamentoKmThreshold,
  });
  return {
    ...d,
    Dispositivo: cleanName(pick(d, ['Dispositivo', 'deviceName', 'name', 'deviceId', 'id'])),
    Placa: safeStr(pick(d, ['Placa', 'placa', 'PLACA_VEICULO'])),
    'Primeiro movimento': dataHoraBR(firstVal, timezone),
    'Último movimento': dataHoraBR(lastVal, timezone),
    'Distância': `${numBR(distNum)} km`,
    'Velocidade Média': normalizeSpeedDisplay(pick(d, ['Velocidade Média', 'Velocidade media', 'averageSpeed']), d?.averageSpeed),
    'Velocidade Média em movimento': normalizeSpeedDisplay(pick(d, ['Velocidade Média em movimento', 'Velocidade media em movimento', 'averageMovingSpeed']), d?.averageMovingSpeed),
    'Velocidade Máxima': normalizeSpeedDisplay(pick(d, ['Velocidade Máxima', 'Velocidade maxima', 'maxSpeed']), d?.maxSpeed),
    'Km inicial': safeStr(pick(d, ['Km inicial', 'Acumulado inicial (rastreador)', 'startOdometer'])) || numBR((Number(d?.startOdometer) || 0) / 1000, 2),
    'Km final': safeStr(pick(d, ['Km final', 'Acumulado final (rastreador)', 'endOdometer'])) || numBR((Number(d?.endOdometer) || 0) / 1000, 2),
    Viagens: String(viagensNum),
    'Motor Ligado': safeStr(pick(d, ['Motor Ligado', 'Motor rodando', 'Horas motor rodando', 'Horas ligado', 'Tempo ligado'])) || minutesToHuman(parseDurationToMinutes(d?.engineHours)),
    'Em movimento': safeStr(emMov) || minutesToHuman(parseDurationToMinutes(d?.duration)),
    'Parado (motor ligado)': safeStr(pick(d, ['Parado (motor ligado)', 'Motor Ocioso', 'Ocioso (motor)', 'Tempo ocioso', 'Tempo parado', 'Tempo em marcha lenta'])) || minutesToHuman(Math.max(0, parseDurationToMinutes(d?.engineHours) - parseDurationToMinutes(d?.duration))),
    'Média de ociosidade por viagem': viagensNum > 0 ? safeStr(pick(d, ['Média de ociosidade por viagem', 'Media de ociosidade por viagem'])) : '—',
    '% ocioso': safeStr(pick(d, ['% ocioso', 'Percentual ocioso', 'Percentual de ociosidade'])),
    status_cliente: status,
    Status: status,
    nivel_cliente: level,
    deviceId: Number(pick(d, ['deviceId', 'id'])) || null,
    __distKm: distNum,
    __trips: viagensNum,
  };
};

export const normalizeClientSummaryItems = (
  items,
  timezone = 'America/Sao_Paulo',
  semDeslocamentoKmThreshold = DEFAULT_SEM_DESLOCAMENTO_KM_THRESHOLD,
) => {
  const mapped = (items || []).map((item) => normalizeRow(item, timezone, semDeslocamentoKmThreshold));
  const byDevice = new Map();
  mapped.forEach((row) => {
    const key = getDeviceStableKey(row);
    const okPriorityBonus = row.status_cliente === 'OK' ? OK_PRIORITY_SCORE_BONUS : 0;
    const score = row.__distKm * 1000
      + row.__trips * 100
      + parseDurationToMinutes(row['Motor Ligado'])
      + parseDurationToMinutes(row['Em movimento'])
      + okPriorityBonus;
    const current = byDevice.get(key);
    if (!current || score > current.__score) byDevice.set(key, { ...row, __score: score });
  });
  return [...byDevice.values()].sort((a, b) => {
    const sa = CLIENT_LEVEL_RANK[a.nivel_cliente] ?? Number.MAX_SAFE_INTEGER;
    const sb = CLIENT_LEVEL_RANK[b.nivel_cliente] ?? Number.MAX_SAFE_INTEGER;
    if (sa !== sb) return sa - sb;
    if (b.__distKm !== a.__distKm) return b.__distKm - a.__distKm;
    if (b.__trips !== a.__trips) return b.__trips - a.__trips;
    const byName = cleanName(a.Dispositivo).localeCompare(cleanName(b.Dispositivo), 'pt-BR');
    if (byName !== 0) return byName;
    return getDeviceStableKey(a).localeCompare(getDeviceStableKey(b), 'pt-BR');
  });
};

export const buildClientSummaryTotals = (rows) => ({
  total: rows.length,
  totalKm: rows.reduce((acc, r) => acc + r.__distKm, 0),
  totalTrips: rows.reduce((acc, r) => acc + r.__trips, 0),
  totalMovMin: rows.reduce((acc, r) => acc + parseDurationToMinutes(r['Em movimento']), 0),
  totalMotorMin: rows.reduce((acc, r) => acc + parseDurationToMinutes(r['Motor Ligado']), 0),
  semDeslocamento: rows.filter((r) => r.nivel_cliente === 'SEM_DESLOCAMENTO').length,
});

export const buildClientRowsFromTrips = ({ trips = [], devices = {} }) => {
  const byDevice = new Map();
  trips.forEach((trip) => {
    const did = trip.deviceId;
    if (!byDevice.has(did)) {
      byDevice.set(did, {
        deviceId: did,
        Dispositivo: cleanName(devices?.[did]?.name || String(did)),
        Placa: safeStr(devices?.[did]?.attributes?.PLACA_VEICULO || ''),
        firstMs: null,
        lastMs: null,
        distKm: 0,
        trips: 0,
        movMin: 0,
        maxSpeed: 0,
        weightedAvgSpeed: 0,
      });
    }
    const row = byDevice.get(did);
    const startMs = ts(trip.startTime);
    const endMs = ts(trip.endTime);
    const durationMs = Math.max(0, (endMs || 0) - (startMs || 0));
    const durationMin = Math.round(durationMs / 60000);
    const distanceKm = (Number(trip.distance) || 0) / 1000;
    const avgSpeed = Number(trip.averageSpeed) || 0;
    row.firstMs = row.firstMs == null ? startMs : Math.min(row.firstMs, startMs || row.firstMs);
    row.lastMs = row.lastMs == null ? endMs : Math.max(row.lastMs, endMs || row.lastMs);
    row.distKm += Math.max(0, distanceKm);
    row.trips += 1;
    row.movMin += Math.max(0, durationMin);
    row.maxSpeed = Math.max(row.maxSpeed, Number(trip.maxSpeed) || 0);
    row.weightedAvgSpeed += avgSpeed * Math.max(1, durationMin);
  });

  const rows = [...byDevice.values()].map((r) => {
    const avgSpeed = r.movMin > 0 ? r.weightedAvgSpeed / r.movMin : 0;
    const { status, level } = getClientStatus({
      distKm: r.distKm,
      trips: r.trips,
      emMovMin: r.movMin,
      semDeslocamentoKmThreshold: 0.5,
    });
    return {
      Dispositivo: r.Dispositivo,
      Placa: r.Placa,
      'Primeiro movimento': r.firstMs ? dataHoraBR(r.firstMs) : '—',
      'Último movimento': r.lastMs ? dataHoraBR(r.lastMs) : '—',
      'Distância': `${numBR(r.distKm)} km`,
      'Velocidade Média': normalizeSpeedDisplay(null, avgSpeed),
      'Velocidade Máxima': normalizeSpeedDisplay(null, r.maxSpeed),
      'Km inicial': '—',
      'Km final': '—',
      Viagens: String(r.trips),
      'Motor Ligado': '—',
      'Em movimento': minutesToHuman(r.movMin),
      'Parado (motor ligado)': '—',
      'Média de ociosidade por viagem': '—',
      '% ocioso': '—',
      status_cliente: status,
      nivel_cliente: level,
      __distKm: r.distKm,
      __trips: r.trips,
    };
  });

  return rows.sort((a, b) => {
    const sa = CLIENT_LEVEL_RANK[a.nivel_cliente] ?? Number.MAX_SAFE_INTEGER;
    const sb = CLIENT_LEVEL_RANK[b.nivel_cliente] ?? Number.MAX_SAFE_INTEGER;
    if (sa !== sb) return sa - sb;
    if (b.__distKm !== a.__distKm) return b.__distKm - a.__distKm;
    if (b.__trips !== a.__trips) return b.__trips - a.__trips;
    const byName = cleanName(a.Dispositivo).localeCompare(cleanName(b.Dispositivo), 'pt-BR');
    if (byName !== 0) return byName;
    return getDeviceStableKey(a).localeCompare(getDeviceStableKey(b), 'pt-BR');
  });
};

const getPointTime = (point) => ts(point?.serverTime);
const getTimeRangeBounds = ({ from, to }) => ({
  fromMs: ts(from),
  toMs: ts(to),
});

const isWithinTimeRange = (pointMs, { fromMs, toMs }) => {
  if (!Number.isFinite(pointMs)) return false;
  if (Number.isFinite(fromMs) && pointMs < fromMs) return false;
  if (Number.isFinite(toMs) && pointMs > toMs) return false;
  return true;
};
export const ROUTE_METRICS_THRESHOLDS = Object.freeze({
  movingSpeedThresholdKnots: 1,
  motionGapToleranceMinutes: 10,
  tripStopToleranceMinutes: 3,
  plausibleMaxSpeedKmh: 180,
  plausibleMaxSegmentDistanceM: 15000,
  plausibleMinDeltaMsForDistance: 15000,
  semDeslocamentoKmThreshold: DEFAULT_SEM_DESLOCAMENTO_KM_THRESHOLD,
});
const DEFAULT_MOVING_SPEED_THRESHOLD_KNOTS = 1;
const KNOT_TO_KMH = 1.852;
const knotsToKmh = (knots) => {
  const speed = Number(knots);
  return Number.isFinite(speed) ? speed * KNOT_TO_KMH : 0;
};
const DEFAULT_MOTION_GAP_TOLERANCE_MINUTES = ROUTE_METRICS_THRESHOLDS.motionGapToleranceMinutes;
const DEFAULT_TRIP_STOP_TOLERANCE_MINUTES = ROUTE_METRICS_THRESHOLDS.tripStopToleranceMinutes;
const PLAUSIBLE_MAX_SPEED_MPS = ROUTE_METRICS_THRESHOLDS.plausibleMaxSpeedKmh / 3.6;
const PLAUSIBLE_MAX_SEGMENT_DISTANCE_M = ROUTE_METRICS_THRESHOLDS.plausibleMaxSegmentDistanceM;
const PLAUSIBLE_MIN_DELTA_MS_FOR_DISTANCE = ROUTE_METRICS_THRESHOLDS.plausibleMinDeltaMsForDistance;

const isDevMode = typeof import.meta !== 'undefined' && Boolean(import.meta?.env?.DEV);

export const calculateDeviceMetricsFromRoute = ({
  routePoints = [],
  tripsCount = 0,
  movingSpeedThreshold = DEFAULT_MOVING_SPEED_THRESHOLD_KNOTS,
  semDeslocamentoKmThreshold = DEFAULT_SEM_DESLOCAMENTO_KM_THRESHOLD,
  motionGapToleranceMinutes = DEFAULT_MOTION_GAP_TOLERANCE_MINUTES,
  tripStopToleranceMinutes = DEFAULT_TRIP_STOP_TOLERANCE_MINUTES,
  from = null,
  to = null,
} = {}) => {
  const ordered = [...(Array.isArray(routePoints) ? routePoints : [])]
    .sort((a, b) => (getPointTime(a) || 0) - (getPointTime(b) || 0));
  // Alinhado à referência docs/reference/n8n-function1-logica-relatorio.js: métricas temporais usam apenas serverTime válido.
  const rangeBounds = getTimeRangeBounds({ from, to });
  const timedOrdered = ordered.filter((point) => {
    const pointMs = getPointTime(point);
    return pointMs !== null && isWithinTimeRange(pointMs, rangeBounds);
  });

  let firstMs = null;
  let lastMs = null;
  let distM = 0;
  let maxSpeed = 0;
  let movMs = 0;
  let motorMs = 0;
  let startOdometerM = null;
  let endOdometerM = null;
  let derivedTrips = 0;
  let inMovement = false;
  let lastMovementEndMs = null;
  const counters = {
    discardedTrackerIncrementInvalid: 0,
    discardedTrackerIncrementTeleport: 0,
    discardedHaversineInvalid: 0,
    discardedHaversineTeleport: 0,
    discardedMotionGap: 0,
    discardedMotorGap: 0,
  };
  const motionGapToleranceMs = Math.max(0, Number(motionGapToleranceMinutes) || 0) * MS_PER_MINUTE;
  const tripStopToleranceMs = Math.max(0, Number(tripStopToleranceMinutes) || 0) * MS_PER_MINUTE;

  timedOrdered.forEach((point, index) => {
    const pointMs = getPointTime(point);
    if (pointMs !== null) {
      firstMs = firstMs == null ? pointMs : Math.min(firstMs, pointMs);
      lastMs = lastMs == null ? pointMs : Math.max(lastMs, pointMs);
    }

    const speedKmh = knotsToKmh(point?.speed);
    if (Number.isFinite(speedKmh)) maxSpeed = Math.max(maxSpeed, speedKmh);

    const totalM = getTrackerTotalMeters(point);
    if (totalM !== null) {
      if (startOdometerM === null) startOdometerM = totalM;
      endOdometerM = totalM;
    }

    const prev = index > 0 ? timedOrdered[index - 1] : null;
    const prevMs = prev ? getPointTime(prev) : null;
    const deltaMs = Math.max(0, (pointMs || 0) - (prevMs || 0));
    const deltaS = deltaMs / 1000;
    const trackerInc = getTrackerIncrementMeters(point);

    const isPlausibleSegment = (distanceM) => {
      if (!Number.isFinite(distanceM) || distanceM < 0) return false;
      if (distanceM === 0) return true;
      if (!Number.isFinite(deltaMs) || deltaMs < PLAUSIBLE_MIN_DELTA_MS_FOR_DISTANCE) return false;
      if (distanceM > PLAUSIBLE_MAX_SEGMENT_DISTANCE_M) return false;
      const estimatedSpeedMps = deltaS > 0 ? distanceM / deltaS : Number.POSITIVE_INFINITY;
      return estimatedSpeedMps <= PLAUSIBLE_MAX_SPEED_MPS;
    };

    if (trackerInc !== null) {
      if (isPlausibleSegment(trackerInc)) {
        distM += trackerInc;
      } else if (deltaMs < PLAUSIBLE_MIN_DELTA_MS_FOR_DISTANCE || trackerInc > PLAUSIBLE_MAX_SEGMENT_DISTANCE_M) {
        counters.discardedTrackerIncrementTeleport += 1;
      } else {
        counters.discardedTrackerIncrementInvalid += 1;
      }
    } else if (prev && pontoConfiavel(point) && pontoConfiavel(prev)) {
      const haversineDistanceM = haversineM(prev.latitude, prev.longitude, point.latitude, point.longitude);
      if (isPlausibleSegment(haversineDistanceM)) {
        distM += haversineDistanceM;
      } else if (deltaMs < PLAUSIBLE_MIN_DELTA_MS_FOR_DISTANCE || haversineDistanceM > PLAUSIBLE_MAX_SEGMENT_DISTANCE_M) {
        counters.discardedHaversineTeleport += 1;
      } else {
        counters.discardedHaversineInvalid += 1;
      }
    }

    if (prev) {
      const prevSpeedKmh = knotsToKmh(prev?.speed);
      const movingSpeedThresholdKmh = knotsToKmh(movingSpeedThreshold);
      const isGapWithinTolerance = motionGapToleranceMs <= 0 || deltaMs <= motionGapToleranceMs;
      const isMoving = getMotion(prev) || (Number.isFinite(prevSpeedKmh) && prevSpeedKmh > movingSpeedThresholdKmh);
      const effectiveMoving = isMoving && isGapWithinTolerance;

      if (effectiveMoving) movMs += deltaMs;
      else if (isMoving) counters.discardedMotionGap += 1;

      if (effectiveMoving) {
        if (!inMovement) {
          if (lastMovementEndMs === null || ((prevMs || pointMs) - lastMovementEndMs) >= tripStopToleranceMs) {
            derivedTrips += 1;
          }
          inMovement = true;
        }
      } else if (inMovement) {
        inMovement = false;
        lastMovementEndMs = prevMs || pointMs;
      }

      if (getIgnition(prev) === true && isGapWithinTolerance) motorMs += deltaMs;
      else if (getIgnition(prev) === true) counters.discardedMotorGap += 1;
    }
  });

  const distKm = Math.max(0, distM / 1000);
  const movMin = Math.round(movMs / 60000);
  const motorMin = Math.round(motorMs / 60000);
  const idleMotorMin = Math.max(0, motorMin - movMin);
  const avgSpeed = movMin > 0 ? distKm / (movMin / 60) : 0;
  const idlePercent = motorMin > 0 ? (idleMotorMin / motorMin) * 100 : null;
  const idleAvgTripMin = tripsCount > 0 ? idleMotorMin / tripsCount : null;
  const { status, level } = getClientStatus({
    distKm,
    trips: tripsCount,
    emMovMin: movMin,
    semDeslocamentoKmThreshold,
  });

  return {
    firstMs,
    lastMs,
    distKm,
    maxSpeed,
    movMin,
    motorMin,
    idleMotorMin,
    avgSpeed,
    idlePercent,
    idleAvgTripMin,
    status,
    level,
    startOdometerM,
    endOdometerM,
    derivedTrips,
    internalAudit: counters,
  };
};

const mapWithConcurrency = async (items, worker, concurrency = 5) => {
  const safeConcurrency = Math.max(1, Number(concurrency) || 1);
  const results = new Array(items.length);
  let cursor = 0;

  const run = async () => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      results[index] = await worker(items[index], index);
    }
  };

  await Promise.all(Array.from({ length: Math.min(safeConcurrency, items.length) }, run));
  return results;
};

export const buildClientRowsFromRoutes = async ({
  deviceIds = [],
  from,
  to,
  devices = {},
  fetchJson,
  concurrency = 5,
  motionGapToleranceMinutes = DEFAULT_MOTION_GAP_TOLERANCE_MINUTES,
  tripStopToleranceMinutes = DEFAULT_TRIP_STOP_TOLERANCE_MINUTES,
  semDeslocamentoKmThreshold = DEFAULT_SEM_DESLOCAMENTO_KM_THRESHOLD,
  enableAuditLog = isDevMode,
}) => {
  const uniqueIds = [...new Set((deviceIds || []).filter(Boolean).map(Number))];
  const perDevice = await mapWithConcurrency(uniqueIds, async (deviceId) => {
    let routePoints = [];
    try {
      const routeQuery = new URLSearchParams({ deviceId, from, to });
      const routeResponse = await fetchJson(`/api/reports/route?${routeQuery.toString()}`);
      routePoints = Array.isArray(routeResponse) ? routeResponse : [];
    } catch (error) {
      console.warn('summary report: failed to load route data', { deviceId, error });
    }

    return { deviceId, routePoints };
  }, concurrency);

  const rows = perDevice.map(({ deviceId, routePoints }) => {
    const metrics = calculateDeviceMetricsFromRoute({
      routePoints,
      tripsCount: 0,
      motionGapToleranceMinutes,
      tripStopToleranceMinutes,
      semDeslocamentoKmThreshold,
      from,
      to,
    });
    const tripsCount = metrics.derivedTrips || 0;
    const { status, level } = getClientStatus({
      distKm: metrics.distKm,
      trips: tripsCount,
      emMovMin: metrics.movMin,
      semDeslocamentoKmThreshold,
    });

    return {
      Dispositivo: cleanName(devices?.[deviceId]?.name || String(deviceId)),
      Placa: safeStr(devices?.[deviceId]?.attributes?.PLACA_VEICULO || ''),
      'Primeiro movimento': metrics.firstMs ? dataHoraBR(metrics.firstMs) : '—',
      'Último movimento': metrics.lastMs ? dataHoraBR(metrics.lastMs) : '—',
      'Distância': `${numBR(metrics.distKm)} km`,
      'Velocidade Média': normalizeSpeedDisplay(null, metrics.avgSpeed),
      'Velocidade Máxima': normalizeSpeedDisplay(null, metrics.maxSpeed),
      'Km inicial': metrics.startOdometerM !== null ? numBR(metrics.startOdometerM / 1000, 2) : '—',
      'Km final': metrics.endOdometerM !== null ? numBR(metrics.endOdometerM / 1000, 2) : '—',
      Viagens: String(tripsCount),
      'Motor Ligado': minutesToHuman(metrics.motorMin),
      'Em movimento': minutesToHuman(metrics.movMin),
      'Parado (motor ligado)': minutesToHuman(Math.max(0, metrics.motorMin - metrics.movMin)),
      'Média de ociosidade por viagem': tripsCount > 0 && metrics.idleAvgTripMin !== null ? minutesToHuman(Math.round(metrics.idleAvgTripMin)) : '—',
      '% ocioso': metrics.idlePercent !== null ? `${numBR(metrics.idlePercent, 2)}%` : '—',
      status_cliente: status,
      nivel_cliente: level,
      __distKm: metrics.distKm,
      __trips: tripsCount,
      __tripsSource: 'route-derived',
      __audit: metrics.internalAudit,
    };
  });

  if (enableAuditLog) {
    const auditTotals = rows.reduce((acc, row) => {
      const counters = row.__audit || {};
      Object.entries(counters).forEach(([key, value]) => {
        acc[key] = (acc[key] || 0) + (Number(value) || 0);
      });
      acc.totalDistanceKm += Number(row.__distKm) || 0;
      acc.totalMovementMinutes += parseDurationToMinutes(row['Em movimento']);
      if (row.nivel_cliente === 'SEM_DESLOCAMENTO') acc.semDeslocamento += 1;
      return acc;
    }, {
      totalDistanceKm: 0,
      totalMovementMinutes: 0,
      semDeslocamento: 0,
    });

    console.info('summary report audit', {
      deviceCount: rows.length,
      auditTotals,
      thresholds: {
        ...ROUTE_METRICS_THRESHOLDS,
        semDeslocamentoKmThreshold,
        motionGapToleranceMinutes,
        tripStopToleranceMinutes,
        movingSpeedThresholdKnots: DEFAULT_MOVING_SPEED_THRESHOLD_KNOTS,
      },
    });
  }

  return rows.sort((a, b) => {
    const sa = CLIENT_LEVEL_RANK[a.nivel_cliente] ?? Number.MAX_SAFE_INTEGER;
    const sb = CLIENT_LEVEL_RANK[b.nivel_cliente] ?? Number.MAX_SAFE_INTEGER;
    if (sa !== sb) return sa - sb;
    if (b.__distKm !== a.__distKm) return b.__distKm - a.__distKm;
    if (b.__trips !== a.__trips) return b.__trips - a.__trips;
    const byName = cleanName(a.Dispositivo).localeCompare(cleanName(b.Dispositivo), 'pt-BR');
    if (byName !== 0) return byName;
    return getDeviceStableKey(a).localeCompare(getDeviceStableKey(b), 'pt-BR');
  });
};
